import { openModal } from '@/store/actions';
import { END } from 'redux-saga';
import { delay, put, select } from 'redux-saga/effects';

export type SagaCallback<DataType = void, ReturnType = unknown> = (args: {
  ok: boolean;
  message?: string;
  data?: DataType;
}) => ReturnType;
export type ExtractCallbackType<T extends (...args: any) => any> = ReturnType<T> extends {
  callback?: SagaCallback<infer R>;
}
  ? R
  : unknown;

const statusManagement = (inputStatus: any = 'inherit') => {
  if (inputStatus == 'inherit') return false;
  if (inputStatus == 'error') return true;
};
export const formatDate = (dateString: string): string => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const date = new Date(dateString);
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day < 10 ? "0" + day : day}, ${year}`;
};

export const canCreateMemory = (invitation: boolean, isPrivate: boolean): boolean => {
  // Allowed only if invitation exists
  if (!invitation) return false;

  // If invited, always allowed (whether private or public)
  return true;
};

/** Match mobile: preview shared stories (including drafts) without an auth token. */
export const shouldUsePublicStoryAccess = (
  invitation?: boolean | string,
  hasToken?: boolean,
): boolean => {
  if (invitation === true || invitation === 'true') return true;
  return !hasToken;
};

export const getFileType = (url: string): string => {
  const extension = url.split('.').pop()?.toLowerCase();

  if (!extension) return "unknown";

  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
  const videoExtensions = ["mp4", "mov", "avi", "mkv", "webm", "flv"];
  const audioExtensions = ["mp3", "wav", "ogg", "aac", "flac", "m4a"];

  if (imageExtensions.includes(extension)) return "image";
  if (videoExtensions.includes(extension)) return "video";
  if (audioExtensions.includes(extension)) return "audio";

  return extension; // Returns raw extension if not categorized
};


export function actionObject<ActionType>(
  type: ActionType,
  payload?: never,
  callback?: never,
  optional?: never,
): { type: typeof type; payload: typeof payload; callback: typeof callback; optional: typeof optional };
export function actionObject<ActionType, PayloadType>(
  type: ActionType,
  payload: PayloadType,
  callback?: never,
  optional?: never,
): { type: typeof type; payload: typeof payload; callback: typeof callback; optional: typeof optional };
export function actionObject<ActionType, PayloadType, CallbackType extends ({ ok, message, value, data }: any) => void>(
  type: ActionType,
  payload: PayloadType,
  callback?: CallbackType,
  optional?: never,
): { type: typeof type; payload: typeof payload; callback: typeof callback; optional: typeof optional };
export function actionObject<
  ActionType,
  PayloadType,
  CallbackType extends ({ ok, message, value, data }: any) => void,
  OptionalType,
>(
  type: ActionType,
  payload: PayloadType,
  callback?: CallbackType,
  optional?: OptionalType,
): { type: typeof type; payload: typeof payload; callback: typeof callback; optional: typeof optional };
export function actionObject(type: any, payload: any, callback: any, optional: any) {
  return { type, payload, callback, optional };
}


export const generateVideoThumbnail = (videoFile: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadeddata = () => {
      video.currentTime = 1;
    };

    video.onseeked = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const thumbnailFile = new File([blob], 'thumbnail.webp', { type: 'image/webp' });
            resolve(thumbnailFile);
          } else {
            reject(new Error('Failed to create thumbnail blob'));
          }
        }, 'image/webp');
      } else {
        reject(new Error('Canvas context is null'));
      }
    };

    video.onerror = () => reject(new Error('Error loading video'));
    video.src = URL.createObjectURL(videoFile);
  });
};








/**
 * Useful to be able to wait for a callback to execute before continuing
 */
export function promisifiedCallback<CallbackDataType = void>(
  callback?: SagaCallback<CallbackDataType>,
): {
  callback: SagaCallback<CallbackDataType>;
  promise: Promise<Parameters<SagaCallback<CallbackDataType>>[0]>;
} {
  let callbackWithContext: SagaCallback<CallbackDataType> = () => { };

  const promise = new Promise((s: (result: Parameters<SagaCallback<CallbackDataType>>[0]) => void) => {
    callbackWithContext = (params) => {
      if (callback) callback(params);
      s(params);
    };
  });
  return {
    promise,
    callback: callbackWithContext,
  };
}


export function* showModal(message = '', type: 'SHOW_MODAL') {
  const { open } = yield select((state: any) => state.intermitence.modal);

  if (!open) {
    yield put(openModal({ content: message }));
  }
}




export function* showDialog(message = '', type = 'success', toast = 'SHOW_TOAST') {
  yield put(
    actionObject(toast, {
      show: true,
      message,
      type,
    }),
  );

  yield delay(3000);

  yield put(
    actionObject(toast, {
      show: false,
    }),
  );
}

export const checkPermissions = (roles: any, permission: any, story_id: any = null) => {
  for (const role of roles) {
    if (role?.story_id === story_id) {
      if (/*role?.story_id === story_id && */ role?.role?.permissions?.find((p: any) => p?.name === permission))
        return true;
      return false;
    }

  }
  return false;
};




export const calculateTotalSize = async (payload: any) => {
  let totalSize = 0;
  const cdnBaseURL = 'https://cdn.memvy.com/';

  /** 1️⃣ Local complementaryMedia files */
  if (Array.isArray(payload.complementaryMedia)) {
    payload.complementaryMedia.forEach((file: File) => {
      if (file?.size) {
        totalSize += file.size;
      }
    });
  }

  /** 2️⃣ Local mediaFiles */
  if (Array.isArray(payload.mediaFiles)) {
    payload.mediaFiles.forEach((file: File) => {
      if (file?.size) {
        totalSize += file.size;
      }
    });
  }

  /** 3️⃣ Remote files in memory_details */
  const { complementaryImage, complementaryAudio, complementaryVideo } = payload.memory_details || {};

  const allRemoteFiles = [
    ...(complementaryImage || []),
    ...(complementaryAudio || []),
    ...(complementaryVideo || []),
  ];

  /** Fetch sizes from remote URLs */
  const remoteSizes = await Promise.all(
    allRemoteFiles.map(async (filePath: string) => {
      try {
        const fullURL = filePath.startsWith('http')
          ? filePath
          : cdnBaseURL + filePath; // Prepend CDN URL if not absolute

        const response = await fetch(fullURL, { method: 'HEAD' });
        const size = parseInt(response.headers.get('Content-Length') || '0', 10);
        return size;
      } catch (error) {
        console.error('Error fetching file size:', error);
        return 0;
      }
    }),
  );

  remoteSizes.forEach((size) => (totalSize += size));


  return totalSize;
};










export const checkRoleAndPermission = (roles: any | any[], roleName: string, permission: string, user_id: any) => {
  const rolesArray = Array.isArray(roles) ? roles : [roles];
  for (const role of rolesArray) {
    if (
      role?.user_id === user_id &&
      role?.role?.name === roleName &&
      role?.role?.permissions?.some((p: any) => p?.name === permission)
    ) {
      return true;
    }
  }
  return false;
};

export const logoutWithFacebook = () => {
  return new Promise((resolve) => {
    let winType: any = window;
    winType?.FB?.logout(function (response: any) {
      resolve(response);
    });
  });
};

export const changeInputStatus = (value: string, error: any) => {
  if (value !== '') {
    if (error) return 'error';
    return 'inherit';
  }
  return 'inherit';
};

export const fileToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

function convertToTitleCase(text: string) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export const findNotificationByMemoryId = (notifications: any, memoryId: number | undefined) => {
  return notifications?.find((notification: any) => notification.actions?.memory_id === memoryId);
};

const worldcountries = [
  { name: 'Afghanistan', id: 'Afghanistan' },
  { name: 'Albania', id: 'Albania' },
  { name: 'Algeria', id: 'Algeria' },
  { name: 'Andorra', id: 'Andorra' },
  { name: 'Angola', id: 'Angola' },
  { name: 'Antigua and Barbuda', id: 'Antigua and Barbuda' },
  { name: 'Argentina', id: 'Argentina' },
  { name: 'Armenia', id: 'Armenia' },
  { name: 'Australia', id: 'Australia' },
  { name: 'Austria', id: 'Austria' },
  { name: 'Azerbaijan', id: 'Azerbaijan' },
  { name: 'Bahamas', id: 'Bahamas' },
  { name: 'Bahrain', id: 'Bahrain' },
  { name: 'Bangladesh', id: 'Bangladesh' },
  { name: 'Barbados', id: 'Barbados' },
  { name: 'Belarus', id: 'Belarus' },
  { name: 'Belgium', id: 'Belgium' },
  { name: 'Belize', id: 'Belize' },
  { name: 'Benin', id: 'Benin' },
  { name: 'Bhutan', id: 'Bhutan' },
  { name: 'Bolivia', id: 'Bolivia' },
  { name: 'Bosnia and Herzegovina', id: 'Bosnia and Herzegovina' },
  { name: 'Botswana', id: 'Botswana' },
  { name: 'Brazil', id: 'Brazil' },
  { name: 'Brunei', id: 'Brunei' },
  { name: 'Bulgaria', id: 'Bulgaria' },
  { name: 'Burkina Faso', id: 'Burkina Faso' },
  { name: 'Burundi', id: 'Burundi' },
  { name: 'Cabo Verde', id: 'Cabo Verde' },
  { name: 'Cambodia', id: 'Cambodia' },
  { name: 'Cameroon', id: 'Cameroon' },
  { name: 'Canada', id: 'Canada' },
  { name: 'Central African Republic', id: 'Central African Republic' },
  { name: 'Chad', id: 'Chad' },
  { name: 'Chile', id: 'Chile' },
  { name: 'China', id: 'China' },
  { name: 'Colombia', id: 'Colombia' },
  { name: 'Comoros', id: 'Comoros' },
  { name: 'Congo, Democratic Republic of the', id: 'Congo, Democratic Republic of the' },
  { name: 'Congo, Republic of the', id: 'Congo, Republic of the' },
  { name: 'Costa Rica', id: 'Costa Rica' },
  { name: 'Croatia', id: 'Croatia' },
  { name: 'Cuba', id: 'Cuba' },
  { name: 'Cyprus', id: 'Cyprus' },
  { name: 'Czech Republic', id: 'Czech Republic' },
  { name: 'Denmark', id: 'Denmark' },
  { name: 'Djibouti', id: 'Djibouti' },
  { name: 'Dominica', id: 'Dominica' },
  { name: 'Dominican Republic', id: 'Dominican Republic' },
  { name: 'Ecuador', id: 'Ecuador' },
  { name: 'Egypt', id: 'Egypt' },
  { name: 'El Salvador', id: 'El Salvador' },
  { name: 'Equatorial Guinea', id: 'Equatorial Guinea' },
  { name: 'Eritrea', id: 'Eritrea' },
  { name: 'Estonia', id: 'Estonia' },
  { name: 'Eswatini', id: 'Eswatini' },
  { name: 'Ethiopia', id: 'Ethiopia' },
  { name: 'Fiji', id: 'Fiji' },
  { name: 'Finland', id: 'Finland' },
  { name: 'France', id: 'France' },
  { name: 'Gabon', id: 'Gabon' },
  { name: 'Gambia', id: 'Gambia' },
  { name: 'Georgia', id: 'Georgia' },
  { name: 'Germany', id: 'Germany' },
  { name: 'Ghana', id: 'Ghana' },
  { name: 'Greece', id: 'Greece' },
  { name: 'Grenada', id: 'Grenada' },
  { name: 'Guatemala', id: 'Guatemala' },
  { name: 'Guinea', id: 'Guinea' },
  { name: 'Guinea-Bissau', id: 'Guinea-Bissau' },
  { name: 'Guyana', id: 'Guyana' },
  { name: 'Haiti', id: 'Haiti' },
  { name: 'Honduras', id: 'Honduras' },
  { name: 'Hungary', id: 'Hungary' },
  { name: 'Iceland', id: 'Iceland' },
  { name: 'India', id: 'India' },
  { name: 'Indonesia', id: 'Indonesia' },
  { name: 'Iran', id: 'Iran' },
  { name: 'Iraq', id: 'Iraq' },
  { name: 'Ireland', id: 'Ireland' },
  { name: 'Israel', id: 'Israel' },
  { name: 'Italy', id: 'Italy' },
  { name: 'Jamaica', id: 'Jamaica' },
  { name: 'Japan', id: 'Japan' },
  { name: 'Jordan', id: 'Jordan' },
  { name: 'Kazakhstan', id: 'Kazakhstan' },
  { name: 'Kenya', id: 'Kenya' },
  { name: 'Kiribati', id: 'Kiribati' },
  { name: 'Korea, North', id: 'Korea, North' },
  { name: 'Korea, South', id: 'Korea, South' },
  { name: 'Kosovo', id: 'Kosovo' },
  { name: 'Kuwait', id: 'Kuwait' },
  { name: 'Kyrgyzstan', id: 'Kyrgyzstan' },
  { name: 'Laos', id: 'Laos' },
  { name: 'Latvia', id: 'Latvia' },
  { name: 'Lebanon', id: 'Lebanon' },
  { name: 'Lesotho', id: 'Lesotho' },
  { name: 'Liberia', id: 'Liberia' },
  { name: 'Libya', id: 'Libya' },
  { name: 'Liechtenstein', id: 'Liechtenstein' },
  { name: 'Lithuania', id: 'Lithuania' },
  { name: 'Luxembourg', id: 'Luxembourg' },
  { name: 'Madagascar', id: 'Madagascar' },
  { name: 'Malawi', id: 'Malawi' },
  { name: 'Malaysia', id: 'Malaysia' },
  { name: 'Maldives', id: 'Maldives' },
  { name: 'Mali', id: 'Mali' },
  { name: 'Malta', id: 'Malta' },
  { name: 'Marshall Islands', id: 'Marshall Islands' },
  { name: 'Mauritania', id: 'Mauritania' },
  { name: 'Mauritius', id: 'Mauritius' },
  { name: 'Mexico', id: 'Mexico' },
  { name: 'Micronesia', id: 'Micronesia' },
  { name: 'Moldova', id: 'Moldova' },
  { name: 'Monaco', id: 'Monaco' },
  { name: 'Mongolia', id: 'Mongolia' },
  { name: 'Montenegro', id: 'Montenegro' },
  { name: 'Morocco', id: 'Morocco' },
  { name: 'Mozambique', id: 'Mozambique' },
  { name: 'Myanmar', id: 'Myanmar' },
  { name: 'Namibia', id: 'Namibia' },
  { name: 'Nauru', id: 'Nauru' },
  { name: 'Nepal', id: 'Nepal' },
  { name: 'Netherlands', id: 'Netherlands' },
  { name: 'New Zealand', id: 'New Zealand' },
  { name: 'Nicaragua', id: 'Nicaragua' },
  { name: 'Niger', id: 'Niger' },
  { name: 'Nigeria', id: 'Nigeria' },
  { name: 'North Macedonia', id: 'North Macedonia' },
  { name: 'Norway', id: 'Norway' },
  { name: 'Oman', id: 'Oman' },
  { name: 'Pakistan', id: 'Pakistan' },
  { name: 'Palau', id: 'Palau' },
  { name: 'Palestine', id: 'Palestine' },
  { name: 'Panama', id: 'Panama' },
  { name: 'Papua New Guinea', id: 'Papua New Guinea' },
  { name: 'Paraguay', id: 'Paraguay' },
  { name: 'Peru', id: 'Peru' },
  { name: 'Philippines', id: 'Philippines' },
  { name: 'Poland', id: 'Poland' },
  { name: 'Portugal', id: 'Portugal' },
  { name: 'Qatar', id: 'Qatar' },
  { name: 'Romania', id: 'Romania' },
  { name: 'Russia', id: 'Russia' },
  { name: 'Rwanda', id: 'Rwanda' },
  { name: 'Saint Kitts and Nevis', id: 'Saint Kitts and Nevis' },
  { name: 'Saint Lucia', id: 'Saint Lucia' },
  { name: 'Saint Vincent and the Grenadines', id: 'Saint Vincent and the Grenadines' },
  { name: 'Samoa', id: 'Samoa' },
  { name: 'San Marino', id: 'San Marino' },
  { name: 'Sao Tome and Principe', id: 'Sao Tome and Principe' },
  { name: 'Saudi Arabia', id: 'Saudi Arabia' },
  { name: 'Senegal', id: 'Senegal' },
  { name: 'Serbia', id: 'Serbia' },
  { name: 'Seychelles', id: 'Seychelles' },
  { name: 'Sierra Leone', id: 'Sierra Leone' },
  { name: 'Singapore', id: 'Singapore' },
  { name: 'Slovakia', id: 'Slovakia' },
  { name: 'Slovenia', id: 'Slovenia' },
  { name: 'Solomon Islands', id: 'Solomon Islands' },
  { name: 'Somalia', id: 'Somalia' },
  { name: 'South Africa', id: 'South Africa' },
  { name: 'South Sudan', id: 'South Sudan' },
  { name: 'Spain', id: 'Spain' },
  { name: 'Sri Lanka', id: 'Sri Lanka' },
  { name: 'Sudan', id: 'Sudan' },
  { name: 'Suriname', id: 'Suriname' },
  { name: 'Sweden', id: 'Sweden' },
  { name: 'Switzerland', id: 'Switzerland' },
  { name: 'Syria', id: 'Syria' },
  { name: 'Taiwan', id: 'Taiwan' },
  { name: 'Tajikistan', id: 'Tajikistan' },
  { name: 'Tanzania', id: 'Tanzania' },
  { name: 'Thailand', id: 'Thailand' },
  { name: 'Timor-Leste', id: 'Timor-Leste' },
  { name: 'Togo', id: 'Togo' },
  { name: 'Tonga', id: 'Tonga' },
  { name: 'Trinidad and Tobago', id: 'Trinidad and Tobago' },
  { name: 'Tunisia', id: 'Tunisia' },
  { name: 'Turkey', id: 'Turkey' },
  { name: 'Turkmenistan', id: 'Turkmenistan' },
  { name: 'Tuvalu', id: 'Tuvalu' },
  { name: 'Uganda', id: 'Uganda' },
  { name: 'Ukraine', id: 'Ukraine' },
  { name: 'United Arab Emirates', id: 'United Arab Emirates' },
  { name: 'United Kingdom', id: 'United Kingdom' },
  { name: 'United States', id: 'United States' },
  { name: 'Uruguay', id: 'Uruguay' },
  { name: 'Uzbekistan', id: 'Uzbekistan' },
  { name: 'Vanuatu', id: 'Vanuatu' },
  { name: 'Vatican City', id: 'Vatican City' },
  { name: 'Venezuela', id: 'Venezuela' },
  { name: 'Vietnam', id: 'Vietnam' },
  { name: 'Yemen', id: 'Yemen' },
  { name: 'Zambia', id: 'Zambia' },
  { name: 'Zimbabwe', id: 'Zimbabwe' },
];



const popularDomains = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'outlook.com',
  'live.com', 'icloud.com', 'yandex.ru', 'mail.ru', 'gmx.de',
];

const translate = (key: any, value: any) => {
  if (!key) return;
  if (value === 'only_letters') return 'This field must only contain letters';
  if (value === 'only_letters_numbers_spaces') return 'This field must only contain letters and numbers and spaces';
  if (value === 'field_required') return 'Field required';

  let valueTranslated = value?.split(key);

  return `This field ${valueTranslated?.[1]}`;
};

export function redirectToLogin() {
  window.location.href = '/app/register';
}

export const formatPastDate = (createdDate: string): string => {
  const now = new Date();
  const date = new Date(createdDate);

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = now.getMonth() - date.getMonth() + (12 * (now.getFullYear() - date.getFullYear()));
  const diffInYears = now.getFullYear() - date.getFullYear();


  if (diffInSeconds < 60) {
    return diffInSeconds === 1 ? '1 second ago' : `${diffInSeconds} seconds ago`;
  }


  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  }


  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }


  if (diffInDays === 1) {
    return 'Yesterday';
  }


  if (diffInDays < 31) {
    return `${date.toLocaleString('default', { month: 'long' })} ${date.getDate()}`;
  }


  if (diffInMonths < 12) {
    return `${date.toLocaleString('default', { month: 'long' })} ${date.getDate()}, ${date.getFullYear()}`;
  }


  return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
};


export function debounce<T extends (...args: any[]) => unknown>(func: T, timeout: number) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}
export const GetInitials = (firstName = '', lastName = '') => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${firstInitial}${lastInitial}`;
};

export const hasUserPurchasedTheStory = (userId: number, storyId: number, userPurchases: any) => {




  for (let purchase of userPurchases) {

    if (purchase.storyId === storyId && purchase.userId === userId) {

      return true;
    }
  }


  return false;
}


export const mapProps = async (store: any, action: any) => {
  store.dispatch(action);
  store.dispatch(END);
  await store.sagaTask.toPromise();
};

export const calculateAspectRatio = (width: any, height: any) => {
  try {

    const gcd = (a: any, b: any): any => {
      return b === 0 ? a : gcd(b, a % b);
    };


    const divisor = gcd(width, height);


    const aspectWidth = width / divisor;
    const aspectHeight = height / divisor;

    return `${aspectWidth}/${aspectHeight}`;
  } catch (error) {
    return null;
  }
};

export function areUrlsEqual(arr1: File[], arr2: File[]): boolean {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
  return arr1.every((file: any, index: any) => file.url === arr2[index]);
}

export const calculateFileSize = (file: any) => {
  if (!file) return 0;
  const fileSizeBytes = file.size;
  return fileSizeBytes;
};




export const faqs = [
  {
    question: "What is the purpose of this FAQ section?",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus fermentum, sapien a convallis vestibulum, libero urna pharetra eros, ut tempus felis lorem vel lorem.",
  },
  {
    question: "How can I get more information?",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean ac elementum elit. In hac habitasse platea dictumst.",
  },
  {
    question: "Who can I contact for support?",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla facilisi. Curabitur scelerisque erat vel nisi facilisis, at tincidunt sapien cursus.",
  },
];
export const generateThumbnailFromURL = (videoUrl: string): Promise<string> => {
  
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = videoUrl;
    video.crossOrigin = "anonymous"; // Avoid CORS issues
    video.muted = true; // Required for autoplay
    video.playsInline = true;
    video.autoplay = true;

    video.onloadeddata = () => {
      video.currentTime = 1; // Capture frame at 1s (adjust if needed)
    };

    video.onseeked = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject("Canvas not supported");
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw frame onto canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 image (JPEG)
      const thumbnail = canvas.toDataURL("image/jpeg");


      resolve(thumbnail);
    };

    video.onerror = () => {
      reject("Error loading video");
    };
  });
};
const templates = [
  {
    "title": "life_story",
    "prompts": [
      "Share a memory of {{storyTitle}} that still makes you smile.",
      "Share something {{storyTitle}} did that you will never forget.",
      "Share the one day you would relive with {{storyTitle}}, and why.",
      "Share any of your favorite memories of {{storyTitle}}."
    ]
  },
  {
    "title": "classmates_story",
    "prompts": [
      "Share the funniest time you had together.",
      "Share who or what made this special.",
      "Share something you would want everyone to remember.",
      "Share any other favorite memories you had together."
    ]
  },
  {
    "title": "teammates_story",
    "prompts": [
      "Share a funny moment that happened on or off the field.",
      "Share a moment that really defined the team.",
      "Share your favorite game and why.",
      "Share any other favorite memories you had together."
    ]
  },
  {
    "title": "misc_story",
    "prompts": [
      "Share the funniest thing that happened.",
      "Share the impact this event had on you.",
      "Share something challenging that happened.",
      "Share any other favorite memories."
    ]
  },
  {
    "title": "wedding_story",
    "prompts": [
      "Share some advice for the new couple.",
      "Share your favorite story of the couple together.",
      "Share the moment you knew they were perfect for each other.",
      "Share any other memories you have of the couple or the wedding."
    ]
  },
  {
    "title": "retirement_story",
    "prompts": [
      "Share something {{storyTitle}} did that demonstrated their leadership/impact.",
      "Share your funniest memory of working with {{storyTitle}}.",
      "Share what {{storyTitle}} meant to the organization.",
      "Share any other favorite memories of {{storyTitle}}."
    ]
  },
  {
    "title": "tribute_story",
    "prompts": [
      "Share a memory with {{storyTitle}} that still makes you smile.",
      "Share something {{storyTitle}} did that you will never forget.",
      "Share a memory of how {{storyTitle}} impacted your life.",
      "Share any of your favorite memories with {{storyTitle}}."
    ]
  },
  {
    "title": "custom_event",
    "prompts": [
      "Share the funniest thing that happened.",
      "Share the impact this event had on you.",
      "Share something challenging that happened.",
      "Share any other favorite memories."
    ]
  },
  {
    "title": "custom_story",
    "prompts": [
      "Share the funniest thing that happened.",
      "Share the impact this event had on you.",
      "Share something challenging that happened.",
      "Share any other favorite memories."
    ]
  },
  {
    "title": "none_of_this_story",
    "prompts": [
      "Share the funniest thing that happened.",
      "Share the impact this event had on you.",
      "Share something challenging that happened.",
      "Share any other favorite memories."
    ]
  },

];

export const makeHasStoryAccess = (
  user: any, story: any, purchases: any
) => {
  if (!user || !story) return false;
  const isOwner = user.id === story.user_id;
  const isAdmin = user?.roles?.some((r: any) => r.story_id === story.id && r.role.name === 'Story_Admin');
  const hasClientPerm = checkPermissions(user?.roles || [], 'CLIENT_STORY_GET', story?.id);
  const hasPurchase = hasUserPurchasedTheStory(user?.id, story?.id, purchases);
  return isOwner || isAdmin || hasClientPerm || hasPurchase;
};

export const hasStoryRole = (
  user: { id?: number; roles?: any[] } | null | undefined,
  storyId: number | string,
  roleNames: string[] = []
): boolean => {
  if (!user?.roles || !storyId) return false;
  const isOwner = user.id === storyId;
  const isAdmin = user?.roles?.some((r: any) => r.story_id === storyId && r.role.name === 'Story_Admin');
  const hasClientPerm = checkPermissions(user?.roles || [], 'CLIENT_MEMORY_CREATE', storyId);
  // if no roleNames provided, just check that user has any role in this story
  if (roleNames.length === 0) {
    return isOwner || isAdmin || hasClientPerm;
  }
  return user?.roles?.some((r: any) => r.story_id === storyId && roleNames.includes(r.role.name)) || false;
};

export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export function putToS3WithProgress(
  url: string,
  blob: Blob,
  onProgress: (percent: number, loaded: number, total: number) => void
): Promise<{ eTag?: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    // Progress events (fires many times)
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        const pct = Math.floor((evt.loaded / evt.total) * 100);
        onProgress(pct, evt.loaded, evt.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const eTag = xhr.getResponseHeader('ETag')?.replaceAll?.('"', '');
        resolve({ eTag });
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during S3 PUT'));
    xhr.send(blob);
  });
}
export function shouldPollForTranscode(updatedMemory: any, originalPayload: any): boolean {
  // still poll if the user just uploaded a new video in this request



  const cv = updatedMemory?.memory_details?.complementaryVideo;
  return Array.isArray(cv) && cv.length > 0;
}

export function safeKeyComponent(name: string) {
  // Keep only A-Z, a-z, 0-9, and underscores — replace everything else with "_"
  return name.replace(/[^a-zA-Z0-9_]+/g, "_");

}

function wait(ms: number) { return new Promise(res => setTimeout(res, ms)); }

export async function loadImageWithRetry(
  url: string,
  maxAttempts = 4,
  firstDelayMs = 600,
  maxDelayMs = 4000
): Promise<string | null> {
  const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

  let attempt = 0;
  let delay = firstDelayMs;

  while (attempt < maxAttempts) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await new Promise<boolean>((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now(); // bust caches during warmup
    });

    if (ok) return url;
    attempt += 1;
    if (attempt >= maxAttempts) break;

    // eslint-disable-next-line no-await-in-loop
    await wait(delay);
    delay = Math.min(Math.floor(delay * 1.6), maxDelayMs);
  }

  return null;
}

export { convertToTitleCase, statusManagement, translate, worldcountries, popularDomains, templates };

